<!--
# Frontend Vue | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadScanTab
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
-->
<template>
  <section class="scan-stage">
    <visitor-lead-live-scanner
      v-if="showLiveScanner"
      :key="liveScannerKey"
      @decode="$emit('decode', $event)"
      @ready="$emit('scanner-ready')"
      @error="$emit('scanner-error', $event)"
    />
    <img
      v-if="capturePreviewUrl"
      :src="capturePreviewUrl"
      alt=""
      class="capture-preview"
    >

    <div class="scan-frame" aria-hidden="true">
      <span class="scan-corner corner-tl"></span>
      <span class="scan-corner corner-tr"></span>
      <span class="scan-corner corner-bl"></span>
      <span class="scan-corner corner-br"></span>
    </div>

    <div class="scan-overlay">
      <div v-if="hasActiveScanLogin" class="scan-login-indicator card shadow-sm">
        <div class="card-body py-2 px-3">
          <div class="small text-uppercase text-muted">{{ t('scan.activeLogin') }}</div>
          <div class="fw-semibold">{{ activeScanUserLabel }}</div>
          <div class="small text-muted">{{ activeScanContextLabel }}</div>
          <button
            class="btn btn-sm btn-outline-secondary rounded-pill mt-2"
            type="button"
            @click="$emit('open-ticket-picker', activeScanFairId)"
          >
            {{ t('actions.changeTicket') }}
          </button>
        </div>
      </div>

      <div v-if="showScanLoginSelector" class="scan-login-selector card shadow">
        <div class="card-body">
          <h2 class="h5 mb-2">{{ t('scan.chooseLoginTitle') }}</h2>
          <p class="text-muted mb-3">{{ t('scan.chooseLoginText') }}</p>

          <div v-if="ticketsLoading" class="text-center py-3">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ t('common.loading') }}</span>
            </div>
          </div>

          <div v-else-if="ticketsError" class="alert alert-danger mb-0">
            {{ ticketsError }}
          </div>

          <div v-else-if="availableTicketFairs.length === 0" class="alert alert-warning mb-0">
            {{ t('ticket.noneFound') }}
          </div>

          <div v-else class="d-flex flex-column gap-3">
            <div class="d-flex gap-2 flex-wrap">
              <button
                v-for="fair in availableTicketFairs"
                :key="fair.id"
                class="btn btn-sm rounded-pill"
                :class="ticketPickerFairId === fair.id ? 'btn-primary' : 'btn-outline-secondary'"
                type="button"
                @click="$emit('open-ticket-picker', fair.id)"
              >
                {{ fair.id }} <span class="small opacity-75">({{ fair.count }})</span>
              </button>
            </div>

            <div v-if="ticketPickerOptions.length > 0" class="d-flex flex-column gap-2">
              <button
                v-for="ticket in ticketPickerOptions"
                :key="ticket.id"
                class="btn btn-outline-secondary text-start"
                type="button"
                @click="$emit('activate-ticket', ticket)"
              >
                <div class="fw-semibold">{{ ticketDisplayTitle(ticket) }}</div>
                <div class="small text-muted">{{ ticketDisplayMeta(ticket) }}</div>
              </button>
            </div>

            <div v-if="hasActiveScanLogin" class="text-end">
              <button
                class="btn btn-sm btn-outline-secondary rounded-pill"
                type="button"
                @click="$emit('close-ticket-picker')"
              >
                {{ t('actions.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="scanMode === 'fallback'" class="scan-actions">
        <button class="btn btn-primary rounded-pill px-4" type="button" @click="triggerCapture">
          {{ t('scan.takePhoto') }}
        </button>
        <p class="capture-hint">
          {{ t('scan.captureHint') }}
        </p>
      </div>
      <div v-else-if="liveScannerFailed" class="scan-actions">
        <div class="alert alert-warning py-2 px-3 mb-0">
          {{ t('scan.liveUnavailable') }}
        </div>
      </div>
    </div>

    <input
      ref="captureInput"
      type="file"
      accept="image/*"
      capture="environment"
      hidden
      @change="$emit('capture-file', $event)"
    >
  </section>
</template>

<script>
import VisitorLeadLiveScanner from '@/components/visitor-lead/VisitorLeadLiveScanner.vue'
import { translateVisitorLead } from '@/components/visitor-lead/i18n.js'

export default {
  name: 'VisitorLeadScanTab',
  components: { VisitorLeadLiveScanner },
  props: {
    hasActiveScanLogin: { type: Boolean, default: false },
    activeScanUserLabel: { type: String, default: '' },
    activeScanContextLabel: { type: String, default: '' },
    activeScanFairId: { type: String, default: '' },
    showScanLoginSelector: { type: Boolean, default: false },
    availableTicketFairs: { type: Array, default: () => [] },
    ticketPickerFairId: { type: String, default: '' },
    ticketPickerOptions: { type: Array, default: () => [] },
    ticketsLoading: { type: Boolean, default: false },
    ticketsError: { type: String, default: '' },
    scanMode: { type: String, default: 'live' },
    showLiveScanner: { type: Boolean, default: false },
    liveScannerKey: { type: Number, default: 0 },
    capturePreviewUrl: { type: String, default: '' },
    liveScannerFailed: { type: Boolean, default: false },
    locale: { type: String, default: 'en' },
  },
  methods: {
    t(key, replacements = {}) {
      return translateVisitorLead(key, replacements, this.locale)
    },
    ticketDisplayTitle(ticket) {
      return ticket.productName || this.t('ticket.fallbackLabel', { barcode: ticket.ticketBarcode })
    },
    ticketDisplayMeta(ticket) {
      return `${ticket.fairId} \u00b7 ${ticket.ticketBarcode}`
    },
    triggerCapture() {
      if (this.$refs.captureInput) {
        this.$refs.captureInput.value = ''
        this.$refs.captureInput.click()
      }
    },
  },
}
</script>

<style scoped>
.scan-stage {
  position: relative;
  overflow: hidden;
  min-height: calc(100vh - 58px - 42px - 60px - env(safe-area-inset-top));
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.16), transparent 30%),
    linear-gradient(180deg, #102035 0%, #1d395c 100%);
}

.capture-preview {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(72vw, 320px);
  height: min(72vw, 320px);
  max-width: calc(100vw - 3rem);
  max-height: calc(100vw - 3rem);
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.42);
  border-radius: 24px;
  pointer-events: none;
}

.scan-corner {
  position: absolute;
  width: 34px;
  height: 34px;
  border-color: #fff;
  border-style: solid;
  border-width: 0;
}

.corner-tl {
  top: 0;
  left: 0;
  border-top-width: 4px;
  border-left-width: 4px;
  border-top-left-radius: 18px;
}

.corner-tr {
  top: 0;
  right: 0;
  border-top-width: 4px;
  border-right-width: 4px;
  border-top-right-radius: 18px;
}

.corner-bl {
  bottom: 0;
  left: 0;
  border-bottom-width: 4px;
  border-left-width: 4px;
  border-bottom-left-radius: 18px;
}

.corner-br {
  right: 0;
  bottom: 0;
  border-right-width: 4px;
  border-bottom-width: 4px;
  border-bottom-right-radius: 18px;
}

.scan-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem calc(1.5rem + env(safe-area-inset-bottom));
  pointer-events: none;
}

.scan-login-indicator {
  align-self: flex-start;
  max-width: min(26rem, calc(100vw - 2rem));
  background: rgba(255, 255, 255, 0.94);
  pointer-events: auto;
}

.scan-login-selector {
  align-self: center;
  width: min(34rem, calc(100vw - 2rem));
  max-height: calc(100vh - 12rem);
  overflow: auto;
  background: rgba(255, 255, 255, 0.96);
  pointer-events: auto;
}

.scan-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 0.75rem;
  pointer-events: auto;
  align-self: center;
}

.capture-hint {
  max-width: 28rem;
  margin: 0;
  padding: 0.75rem 0.9rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  text-align: center;
  font-size: 0.9rem;
  backdrop-filter: blur(8px);
}
</style>
